/**
 * The single HTTP client for the whole app.
 *
 * Every network call goes through here, which is what makes these behaviours
 * automatic rather than something each screen has to remember:
 *
 *  - the Authorization header, from the in-memory access token;
 *  - `X-Tenant-Id`, from the selected organization;
 *  - transparent refresh on a 401, with concurrent 401s sharing ONE refresh
 *    call rather than each firing their own (which would trip the server's
 *    token-reuse detection and sign everyone out);
 *  - unwrapping the { success, data } envelope;
 *  - turning an error envelope into a typed ApiError carrying the machine
 *    code, the field-level details and the request id.
 *
 * The base URL is the API GATEWAY. The browser never learns an internal
 * service URL.
 */
import { clearTokens, getAccessToken, getRefreshToken, getTenantId, setTokens } from './tokenStore.js';

const BASE_URL = (import.meta.env?.VITE_API_URL ?? 'http://localhost:8000/api/v1').replace(/\/$/, '');

/** Endpoints that must never trigger a refresh attempt. */
const NO_REFRESH = [
  '/auth/login',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
];

/**
 * Path trees that must never trigger a refresh-and-sign-out.
 *
 * The external vendor RFQ portal is unauthenticated: whoever is on it has no
 * NexERP session, so there is nothing to refresh and nothing to sign out of.
 * Without this, a vendor hitting a 401 would fire a pointless refresh, and a
 * SIGNED-IN colleague who happened to open a vendor link in the same browser
 * would be thrown out of the ERP by a page that has nothing to do with them.
 */
const NO_REFRESH_PREFIXES = ['/rfq-portal/', '/quote-portal/', '/customer-portal/', '/crm/public/'];

const skipsRefresh = (path) =>
  NO_REFRESH.includes(path) || NO_REFRESH_PREFIXES.some((prefix) => path.startsWith(prefix));

export class ApiError extends Error {
  constructor(message, { status, code, details, requestId } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Zod field errors, keyed by field, for inline form messages. */
  get fieldErrors() {
    if (!Array.isArray(this.details)) return {};
    return this.details.reduce((map, issue) => {
      if (issue?.field && !map[issue.field]) map[issue.field] = issue.message;
      return map;
    }, {});
  }

  get isValidation() {
    return this.code === 'VALIDATION_ERROR' || this.status === 422;
  }
  get isAuth() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isConflict() {
    return this.status === 409;
  }
}

/** Raised when the session cannot be recovered; the app signs the user out. */
export class SessionExpiredError extends ApiError {
  constructor() {
    super('Your session has expired. Please sign in again.', { status: 401, code: 'SESSION_EXPIRED' });
    this.name = 'SessionExpiredError';
  }
}

// --- shared refresh --------------------------------------------------------
// A page load can fire several requests at once. If each retried its own
// refresh, the second would present an already-rotated token, which the server
// correctly treats as theft and answers by revoking every session. So all
// waiters share one in-flight refresh.
let refreshInFlight = null;
const sessionExpiredHandlers = new Set();

/** Lets AuthContext react to an unrecoverable session. */
export function onSessionExpired(handler) {
  sessionExpiredHandlers.add(handler);
  return () => sessionExpiredHandlers.delete(handler);
}

function announceSessionExpired() {
  clearTokens();
  for (const handler of sessionExpiredHandlers) handler();
}

async function refreshSession() {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    announceSessionExpired();
    throw new SessionExpiredError();
  }

  refreshInFlight = (async () => {
    try {
      let payload = await postRefresh(refreshToken);

      // `refreshInFlight` only coordinates THIS tab. Another tab sharing the
      // same localStorage can rotate the token underneath us, and the copy we
      // just sent is then a spent one — which the server is right to refuse.
      //
      // So before concluding the session is dead, look again at what is
      // stored. If it has changed, the other tab succeeded and left us a
      // working token: use it. Signing out here is what made a second tab
      // enough to throw the user back to the login screen.
      if (!payload?.success) {
        const current = getRefreshToken();
        if (current && current !== refreshToken) {
          payload = await postRefresh(current);
        }
      }

      if (!payload?.success) {
        announceSessionExpired();
        throw new SessionExpiredError();
      }

      setTokens({ accessToken: payload.data.accessToken, refreshToken: payload.data.refreshToken });
      return payload.data.accessToken;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** One refresh attempt. Returns the parsed body, or null on any failure. */
async function postRefresh(refreshToken) {
  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  const payload = await response.json().catch(() => null);
  return response.ok && payload?.success ? payload : null;
}

function buildHeaders({ body, tenantId, skipTenant, headers: extra }) {
  const headers = {};

  if (body !== undefined) headers['content-type'] = 'application/json';

  const token = getAccessToken();
  if (token) headers.authorization = `Bearer ${token}`;

  // The organization the user is working in. Untrusted by the server, which
  // revalidates it against their memberships on every request.
  const tenant = tenantId ?? (skipTenant ? null : getTenantId());
  if (tenant) headers['x-tenant-id'] = tenant;

  // Per-call headers, for the one surface that needs a credential this client
  // knows nothing about: the external vendor RFQ portal sends X-RFQ-Session,
  // which belongs to a vendor with no NexERP account and therefore has no
  // business living in the token store beside a signed-in user's session.
  for (const [key, value] of Object.entries(extra ?? {})) {
    if (value !== undefined && value !== null && value !== '') headers[key.toLowerCase()] = String(value);
  }

  return headers;
}

async function parse(response) {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    // A non-JSON body means something in front of the API answered (a proxy,
    // a tunnel, a captive portal). Surface it cleanly rather than crashing.
    throw new ApiError('The server returned an unexpected response', {
      status: response.status,
      code: 'BAD_RESPONSE',
    });
  }
}

// --- a service that is restarting -----------------------------------------
// These three mean "the gateway could not get an answer out of a service",
// which in practice means a service that is restarting: a deploy, a crash and
// respawn, or `node --watch` picking up a saved file in development. Every
// socket it had is dropped the instant it goes, and any request in flight at
// that moment comes back as one of these.
//
// The gateway re-sends the forward itself, so this only has to cover a restart
// that outlasted its window. It is worth covering, because the request that
// keeps catching it is GET /auth/me one round trip after a successful sign-in,
// and a hard failure there leaves somebody staring at an error on the login
// screen for a session that is in fact perfectly good.
//
// READS ONLY, for the same reason the gateway replays reads only: a write that
// reached the service may have been carried out before it died.
const RESTART_STATUSES = [502, 503, 504];

/** The server's own Retry-After, capped so a click never hangs on it. */
function retryAfterMs(response) {
  const seconds = Number(response.headers.get('retry-after'));
  if (Number.isFinite(seconds) && seconds > 0) return Math.min(seconds * 1000, 1500);
  return 400;
}

function toError(response, payload) {
  const error = payload?.error ?? {};
  const message =
    error.message ??
    (response.status >= 500
      ? 'Something went wrong on our side. Please try again.'
      : 'The request could not be completed.');

  return new ApiError(message, {
    status: response.status,
    code: error.code,
    details: error.details,
    requestId: payload?.requestId,
  });
}

/**
 * @param {string} path e.g. '/purchase/suppliers'
 * @param {object} [options]
 * @param {string} [options.method]
 * @param {object} [options.body]        serialised as JSON
 * @param {object} [options.query]       appended, skipping empty values
 * @param {string} [options.tenantId]    overrides the selected organization
 * @param {boolean} [options.skipTenant] omit X-Tenant-Id entirely
 * @param {AbortSignal} [options.signal]
 * @param {boolean} [options.raw]        return the whole envelope, for pagination
 */
export async function apiRequest(path, options = {}) {
  const {
    method = 'GET',
    body,
    query,
    tenantId,
    skipTenant,
    headers: extraHeaders,
    signal,
    raw = false,
    _retried = false,
    _restartRetried = false,
  } = options;

  const url = new URL(`${BASE_URL}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }

  let response;
  try {
    response = await fetch(url, {
      method,
      headers: buildHeaders({ body, tenantId, skipTenant, headers: extraHeaders }),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    // The gateway is unreachable, or the browser is offline.
    throw new ApiError('Cannot reach the server. Check your connection and try again.', {
      status: 0,
      code: 'NETWORK_ERROR',
    });
  }

  const payload = await parse(response);

  if (response.ok) return raw ? payload : payload?.data ?? null;

  const error = toError(response, payload);

  // One more try for a read caught by a restarting service. See
  // RESTART_STATUSES above for why this is reads only and only once.
  if (method === 'GET' && !_restartRetried && RESTART_STATUSES.includes(error.status)) {
    const wait = retryAfterMs(response);
    await new Promise((resolve) => setTimeout(resolve, wait));
    if (!signal?.aborted) return apiRequest(path, { ...options, _restartRetried: true });
  }

  // A 401 carries two entirely different meanings, and telling them apart is
  // the difference between a useful message and a user being thrown out.
  //
  //  - INVALID_CREDENTIALS means a password IN THE REQUEST BODY was wrong.
  //    The session is perfectly healthy. Refreshing it, replaying the same
  //    wrong password and then concluding the session is dead is what used to
  //    happen on /auth/change-password: mistyping the temporary password
  //    signed the user out mid-onboarding, and the screen unmounted before it
  //    could render the reason. So: no refresh, no retry, no sign-out - just
  //    surface it and let the form show it.
  //
  //  - anything else means the ACCESS TOKEN was rejected, which is exactly
  //    what a refresh-and-retry is for.
  if (error.status === 401 && error.code !== 'INVALID_CREDENTIALS' && !skipsRefresh(path)) {
    if (!_retried && getRefreshToken()) {
      await refreshSession();
      return apiRequest(path, { ...options, _retried: true });
    }
    announceSessionExpired();
  }

  throw error;
}

// --- in-flight GET coalescing ----------------------------------------------
// Two callers asking for the same thing at the same moment should cost ONE
// request, not two. That happens constantly: a list and the dropdown beside it,
// a context reloading while a screen mounts, and — every single time, in
// development — React StrictMode mounting each component twice.
//
// Only GETs are shared. Coalescing a POST would silently drop a second write.
//
// A caller's own AbortSignal still rejects THAT caller (useApi depends on it to
// discard a stale result); it simply no longer cancels a response another
// component is still waiting for.
const inFlightGets = new Map();

const abortError = () => new DOMException('The request was aborted', 'AbortError');

/** A stable key for a query object, so { a, b } and { b, a } match. */
function queryKey(query) {
  if (!query) return '';
  return Object.entries(query)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => [key, String(value)])
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, value]) => key + '=' + value)
    .join('&');
}

function sharedGet(path, options = {}) {
  const { signal, ...rest } = options;

  // The tenant is part of the identity of a GET: the same path in another
  // organization is a different request and must never share a response.
  const tenant = rest.tenantId ?? (rest.skipTenant ? null : getTenantId());
  // Custom headers are part of a GET's identity too: two calls differing only
  // by X-RFQ-Session are two different callers and must not share a response.
  const key = JSON.stringify([
    path,
    queryKey(rest.query),
    tenant ?? null,
    Boolean(rest.raw),
    rest.headers ? queryKey(rest.headers) : null,
  ]);

  let shared = inFlightGets.get(key);
  if (!shared) {
    shared = apiRequest(path, { ...rest, method: 'GET' });
    inFlightGets.set(key, shared);
    // Cleared the moment it settles, so this is a coalescing window and not a
    // cache — the next call always goes to the network.
    shared.catch(() => { }).then(() => inFlightGets.delete(key));
  }

  if (!signal) return shared;
  if (signal.aborted) return Promise.reject(abortError());

  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    shared.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });
}

export const api = {
  get: (path, options) => sharedGet(path, options),
  /** Returns the full envelope, so the caller can read `pagination`. */
  list: (path, query, options) => sharedGet(path, { ...options, query, raw: true }),
  post: (path, body, options) => apiRequest(path, { ...options, method: 'POST', body }),
  put: (path, body, options) => apiRequest(path, { ...options, method: 'PUT', body }),
  patch: (path, body, options) => apiRequest(path, { ...options, method: 'PATCH', body }),
  delete: (path, options) => apiRequest(path, { ...options, method: 'DELETE' }),
};

export const apiBaseUrl = BASE_URL;
